import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { useCategories, useScenarios } from '../store/contentStore';
import { useAppStore } from '../store/appStore';
import type { AgeGroup } from '../types';

type FilterAge = AgeGroup | 'all';

const AGE_FILTERS: { label: string; value: FilterAge }[] = [
  { label: 'All Ages', value: 'all' },
  { label: '4–6', value: '4-6' },
  { label: '7–9', value: '7-9' },
  { label: '10–12', value: '10-12' },
];

function deriveDefaultFilters(childProfiles: { ageGroup: AgeGroup }[]): Set<FilterAge> {
  if (!childProfiles.length) return new Set<FilterAge>(['all']);
  const ages = [...new Set(childProfiles.map((c) => c.ageGroup))] as FilterAge[];
  return new Set(ages);
}

export default function Library() {
  const navigate = useNavigate();
  const categories = useCategories();
  const scenarios = useScenarios();
  const user = useAppStore((s) => s.user);

  const [search, setSearch] = useState('');

  // Multi-select age filter — defaults to child's age(s)
  const [selectedAges, setSelectedAges] = useState<Set<FilterAge>>(() =>
    deriveDefaultFilters(user?.childProfiles ?? [])
  );

  const isAllSelected = selectedAges.has('all') || selectedAges.size === 0;

  const toggleAge = (value: FilterAge) => {
    if (value === 'all') {
      setSelectedAges(new Set<FilterAge>(['all']));
      return;
    }
    setSelectedAges((prev) => {
      const next = new Set(prev);
      next.delete('all');
      if (next.has(value)) {
        next.delete(value);
        if (next.size === 0) next.add('all');
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const scenarioMatchesFilter = (ageGroup: AgeGroup) =>
    isAllSelected || selectedAges.has(ageGroup);

  const getScenarioCount = (categoryId: string) =>
    scenarios.filter(
      (s) => s.categoryId === categoryId && scenarioMatchesFilter(s.ageGroup)
    ).length;

  const filteredCategories = useMemo(() => {
    const q = search.toLowerCase().trim();
    return categories.filter((cat) => {
      const matchesSearch =
        !q ||
        cat.name.toLowerCase().includes(q) ||
        cat.description.toLowerCase().includes(q);
      const hasScenarios = getScenarioCount(cat.id) > 0;
      return matchesSearch && hasScenarios;
    });
  }, [search, selectedAges, categories, scenarios]);

  return (
    <div className="px-4 pt-4 pb-24">
      {/* Header */}
      <div className="pt-4 mb-5">
        <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-800">
          Library
        </h1>
        <p className="text-gray-400 text-sm mt-0.5">
          Browse all life skills by category
        </p>
      </div>

      {/* Search bar */}
      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search categories..."
          className="w-full bg-white border border-gray-100 rounded-2xl pl-10 pr-10 py-3 text-sm text-gray-700 placeholder-gray-300 shadow-sm outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-all"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Age filter tabs — multi-select */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1 scrollbar-none">
        {AGE_FILTERS.map((f) => {
          const active =
            f.value === 'all' ? isAllSelected : selectedAges.has(f.value);
          return (
            <button
              key={f.value}
              onClick={() => toggleAge(f.value)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                active
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-white text-gray-500 border border-gray-100 hover:border-gray-200'
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Category grid */}
      {filteredCategories.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">🔍</div>
          <p className="font-semibold text-gray-500">No categories found</p>
          <p className="text-sm mt-1">Try a different search or age filter</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filteredCategories.map((cat, i) => {
            const count = getScenarioCount(cat.id);
            return (
              <motion.button
                key={cat.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: i * 0.05,
                  type: 'spring',
                  stiffness: 240,
                  damping: 22,
                }}
                whileTap={{ scale: 0.96 }}
                onClick={() => navigate(`/library/category/${cat.id}`)}
                className="text-left rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-200 group"
              >
                <div
                  className={`bg-gradient-to-br ${cat.color} p-5 flex flex-col`}
                >
                  <span className="text-4xl mb-3">{cat.icon}</span>
                  <h3 className="font-display font-bold text-white text-sm leading-tight mb-1">
                    {cat.name}
                  </h3>
                  <p className="text-white/70 text-xs leading-snug line-clamp-2 mb-3">
                    {cat.description}
                  </p>
                  <span className="self-start bg-white/20 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                    {count} scenario{count !== 1 ? 's' : ''}
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}
